//libs
import React, { Dispatch, memo, SetStateAction, useRef, useState } from 'react';

//styles
import {
  DesktopContainer,
  Image,
  InnerContainer,
  SelectSize,
  SizeOptions,
  SizeOptionsContainer,
  SizeOptionsInnerContainer,
  SizeSelectionContainer,
  SubTitle,
  Title,
} from 'shared/layouts/Wishlist/components/SizeSelector/styles';

//hooks
import { useOutsideClickHandler } from 'shared/hooks/useOutsideClickHandler';

//components
import Price from 'shared/components/Product/components/Price';
import IOElement from 'shared/components/IOElement';
import Notify from 'shared/components/Notify/Mobile';

//types
interface Desktop {
  product: Listing.IProduct | undefined;
  onHide: VoidFunction;
  children: React.ReactNode;
  setSelectedSize: Dispatch<SetStateAction<ProductDetail.ISizeOption | undefined>>;
  selectedSize: ProductDetail.ISizeOption | undefined;
  shake: boolean;
}

const DeskTopSizeSelector = ({
  product,
  onHide,
  children,
  setSelectedSize,
  shake = false,
  selectedSize,
}: Desktop) => {
  const ref = useRef<HTMLElement>(null);
  useOutsideClickHandler(ref, () => {
    onHide();
  });

  const scrollRef = useRef<HTMLDivElement | null>(null);
  const [isScrolled, setIsScrolled] = useState(false);

  const selectedProduct = selectedSize ? selectedSize : product;

  const isOneSize = product?.type !== 'configurable';

  if (!product) return null;
  return (
    <DesktopContainer isOutOfStock={product.isOutOfStock} ref={ref} isSizeSelected={!!selectedSize}>
      <InnerContainer>
        <Image alt={selectedProduct?.title} src={product.imageUrl} aspectRatio={0.75} />
        <SizeSelectionContainer>
          <Title>{selectedProduct?.title}</Title>
          <SubTitle>{selectedProduct?.subTitle}</SubTitle>
          <Price
            discount={selectedProduct?.discount}
            discountedPrice={selectedProduct?.discountedPrice}
            price={selectedProduct?.price}
          />

          {product.isOutOfStock && isOneSize ? (
            <>
              <Notify
                showInlineform
                showHelperText={false}
                className={'desktop-notify'}
                productId={product?.productId}
              />
            </>
          ) : (
            <>
              <SelectSize isScrolled={isScrolled}>Select Size</SelectSize>
              <SizeOptionsContainer>
                <SizeOptionsInnerContainer ref={scrollRef}>
                  <SizeOptions
                    isScrolled={isScrolled}
                    className="size-options"
                    shake={shake}
                    onSelect={setSelectedSize}
                    sizeOptions={product?.sizeOptions?.sizes}
                    currentSize={selectedSize}
                    hideBodyMeasurements={false}
                  >
                    <IOElement
                      options={{ root: scrollRef.current }}
                      onVisible={() => setIsScrolled(false)}
                      onHidden={() => setIsScrolled(true)}
                    />
                  </SizeOptions>
                </SizeOptionsInnerContainer>
                {children}
              </SizeOptionsContainer>
            </>
          )}
        </SizeSelectionContainer>
      </InnerContainer>
    </DesktopContainer>
  );
};

export default memo(DeskTopSizeSelector);
